import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { type Author, type AuthorType } from "@/mocks/articles";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAuthorsDB } from "@/hooks/useAuthorsDB";
import { useAuthorAvatarUpload } from "@/hooks/useAuthorAvatarUpload";
import { useAdminI18n } from "@/hooks/useAdminI18n";
import { supabase } from "@/lib/supabase";

const authorTypeLabels: Record<AuthorType, string> = {
  redaktion: "Redaktion",
  autor: "Autor",
  kolumnist: "Kolumnist",
  gastautor: "Gastautor",
  editor: "Editor",
};

const authorTypeColors: Record<AuthorType, string> = {
  redaktion: "bg-sky-100 text-sky-700",
  autor: "bg-emerald-100 text-emerald-700",
  kolumnist: "bg-rose-100 text-rose-700",
  gastautor: "bg-violet-100 text-violet-700",
  editor: "bg-amber-100 text-amber-700",
};

const socialIcons: Record<string, string> = {
  twitter: "ri-twitter-x-line",
  linkedin: "ri-linkedin-line",
  instagram: "ri-instagram-line",
  github: "ri-github-line",
  facebook: "ri-facebook-line",
  youtube: "ri-youtube-line",
};


const emptyAuthor: Omit<Author, "articleCount"> & { articleCount: number } = {
  name: "",
  slug: "",
  role: "",
  shortBio: "",
  longBio: "",
  email: "",
  image: "",
  articleCount: 0,
  author_type: "autor",
  isActive: true,
  socialLinks: [],
};

interface AuthorArticleInfo {
  count: number;
  latestTitle: string | null;
  latestSlug: string | null;
  latestDate: string | null;
}

function AvatarPlaceholder({ name, size = 40 }: { name: string; size?: number }) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="flex items-center justify-center bg-hj-gray-light text-hj-gray font-bold shrink-0"
      style={{ width: size, height: size, borderRadius: size / 2, fontSize: size * 0.4 }}
    >
      {initials || <i className="ri-user-line" style={{ fontSize: size * 0.4 }}></i>}
    </div>
  );
}

export default function AdminAuthors() {
  const { authors: dbAuthors, loading, error: dbError, createAuthor, updateAuthor, deleteAuthor: dbDelete } = useAuthorsDB();
  const { uploadAvatar, removeAvatar, progress: uploadProgress } = useAuthorAvatarUpload();
  const { t } = useAdminI18n();

  const typeTabOptions = [
    { value: "all" as const, label: t("authors.type.all") },
    { value: "redaktion" as const, label: t("authors.type.redaktion") },
    { value: "autor" as const, label: t("authors.type.autor") },
    { value: "kolumnist" as const, label: t("authors.type.kolumnist") },
    { value: "gastautor" as const, label: t("authors.type.gastautor") },
    { value: "editor" as const, label: t("authors.type.editor") },
  ];

  const [authorsList, setAuthorsList] = useState<Author[]>([]);
  const [articleInfos, setArticleInfos] = useState<Record<string, AuthorArticleInfo>>();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<AuthorType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<Author | null>(null);
  const [formData, setFormData] = useState(emptyAuthor);
  const [deleteModal, setDeleteModal] = useState<Author | null>(null);
  const [activeTab, setActiveTab] = useState<"general" | "bio" | "social">("general");
  const [newSocial, setNewSocial] = useState({ platform: "twitter", url: "" });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Load authors from DB
  useEffect(() => {
    setAuthorsList(dbAuthors);
  }, [dbAuthors]);

  // Real article counts + latest article per author
  const fetchArticleInfos = useCallback(async () => {
    if (authorsList.length === 0) return;
    const slugs = authorsList.map((a) => a.slug);
    const { data } = await supabase
      .from("articles")
      .select("author_slug, title, slug, published_at")
      .eq("status", "published")
      .in("author_slug", slugs)
      .order("published_at", { ascending: false });

    const infos: Record<string, AuthorArticleInfo> = {};
    (data ?? []).forEach((row) => {
      const slug = (row as Record<string, unknown>).author_slug as string;
      const title = (row as Record<string, unknown>).title as string;
      const articleSlug = (row as Record<string, unknown>).slug as string;
      const publishedAt = (row as Record<string, unknown>).published_at as string;
      if (!infos[slug]) {
        infos[slug] = { count: 0, latestTitle: null, latestSlug: null, latestDate: null };
      }
      infos[slug].count += 1;
      if (!infos[slug].latestTitle) {
        infos[slug].latestTitle = title;
        infos[slug].latestSlug = articleSlug;
        infos[slug].latestDate = publishedAt;
      }
    });
    setArticleInfos(infos);
  }, [authorsList]);

  useEffect(() => {
    fetchArticleInfos();
  }, [fetchArticleInfos]);

  const getInfo = (slug: string): AuthorArticleInfo =>
    (articleInfos && articleInfos[slug]) || { count: 0, latestTitle: null, latestSlug: null, latestDate: null };

  const filtered = useMemo(() => {
    return authorsList.filter((a) => {
      const matchesSearch =
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.role.toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase()) ||
        a.shortBio.toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === "all" || a.author_type === typeFilter;
      const matchesStatus = statusFilter === "all" || (statusFilter === "active" ? a.isActive : !a.isActive);
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [authorsList, search, typeFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: authorsList.length,
      active: authorsList.filter((a) => a.isActive).length,
      redaktion: authorsList.filter((a) => a.author_type === "redaktion").length,
      autor: authorsList.filter((a) => a.author_type === "autor").length,
      kolumnist: authorsList.filter((a) => a.author_type === "kolumnist").length,
      gastautor: authorsList.filter((a) => a.author_type === "gastautor").length,
      editor: authorsList.filter((a) => a.author_type === "editor").length,
    };
  }, [authorsList]);

  const openCreate = () => {
    setEditingAuthor(null);
    setFormData(emptyAuthor);
    setActiveTab("general");
    setAvatarError(null);
    setModalOpen(true);
  };

  const openEdit = (author: Author) => {
    setEditingAuthor(author);
    setFormData({ ...author });
    setActiveTab("general");
    setAvatarError(null);
    setModalOpen(true);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    setAvatarUploading(true);
    const slug = formData.slug || generateSlug(formData.name) || "neu";
    const { url, error } = await uploadAvatar(file, slug);
    setAvatarUploading(false);
    if (error) {
      setAvatarError(error);
    } else if (url) {
      // Remove old avatar from storage if it exists
      if (formData.image && formData.image.includes("/avatars/")) {
        await removeAvatar(formData.image);
      }
      setFormData((prev) => ({ ...prev, image: url }));
    }
    e.target.value = "";
  };

  const handleRemoveAvatar = async () => {
    if (formData.image && formData.image.includes("/avatars/")) {
      await removeAvatar(formData.image);
    }
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) return;
    setSaveError(null);
    if (editingAuthor) {
      const { error } = await updateAuthor(editingAuthor.slug, formData);
      if (error) { setSaveError(error); return; }
    } else {
      const { error } = await createAuthor(formData);
      if (error) { setSaveError(error); return; }
    }
    setModalOpen(false);
  };

  const handleDelete = async () => {
    if (deleteModal) {
      await dbDelete(deleteModal.slug);
      setDeleteModal(null);
    }
  };

  const addSocialLink = () => {
    if (!newSocial.url.trim()) return;
    setFormData((prev) => ({
      ...prev,
      socialLinks: [...prev.socialLinks, { ...newSocial }],
    }));
    setNewSocial({ platform: "twitter", url: "" });
  };

  const removeSocialLink = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index),
    }));
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-")
      .substring(0, 50);
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("authors.title")}</h1>
            {dbError && <p className="text-xs text-red-600 mt-1">Hata: {dbError}</p>}
            {!dbError && <p className="text-sm text-hj-gray mt-1">Tüm yazar türlerini tek yerden yönetin</p>}
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t("authors.newAuthor")}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
          {[
            { label: "Toplam", value: stats.total, icon: "ri-team-line", color: "bg-slate-50" },
            { label: "Aktif", value: stats.active, icon: "ri-user-follow-line", color: "bg-emerald-50" },
            { label: "Redaktion", value: stats.redaktion, icon: "ri-user-settings-line", color: "bg-sky-50" },
            { label: "Autoren", value: stats.autor, icon: "ri-user-line", color: "bg-emerald-50" },
            { label: "Kolumnisten", value: stats.kolumnist, icon: "ri-user-star-line", color: "bg-rose-50" },
            { label: "Gastautoren", value: stats.gastautor, icon: "ri-user-shared-line", color: "bg-violet-50" },
            { label: "Editörler", value: stats.editor, icon: "ri-edit-box-line", color: "bg-amber-50" },
          ].map((s) => (
            <div key={s.label} className={`${s.color} rounded-lg border border-hj-gray-border p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <i className={`${s.icon} text-hj-gray`}></i>
                <span className="text-xs text-hj-gray font-medium">{s.label}</span>
              </div>
              <p className="text-2xl font-bold text-hj-dark">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-1 mb-4 flex flex-wrap gap-1">
          {typeTabOptions.map((tab) => {
            const isActive = typeFilter === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setTypeFilter(tab.value)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? "bg-hj-red text-white"
                    : "text-hj-gray hover:bg-hj-gray-light hover:text-hj-dark"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Search + Status */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Yazar ara..."
                className="w-full pl-9 pr-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
            >
              <option value="all">Tümü Durum</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-80 rounded-xl bg-hj-gray-light animate-pulse"></div>
            ))}
          </div>
        )}

        {/* Author Cards */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filtered.map((author) => {
              const info = getInfo(author.slug);
              return (
                <div
                  key={author.slug}
                  className="bg-white rounded-xl border border-hj-gray-border overflow-hidden hover:border-hj-red/30 transition-all hover:shadow-sm"
                >
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="shrink-0">
                        {author.image ? (
                          <img
                            src={author.image}
                            alt={author.name}
                            loading="lazy"
                            width={80}
                            height={80}
                            className="w-20 h-20 rounded-full object-cover border-2 border-hj-gray-border"
                          />
                        ) : (
                          <AvatarPlaceholder name={author.name} size={80} />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* Name + badges */}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-base font-bold text-hj-dark">{author.name}</h3>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${authorTypeColors[author.author_type]}`}>
                            {authorTypeLabels[author.author_type]}
                          </span>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${author.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                            {author.isActive ? "Aktif" : "Pasif"}
                          </span>
                        </div>

                        {/* Role */}
                        <p className="text-sm text-hj-red font-medium">{author.role}</p>

                        {/* Bio excerpt */}
                        <p className="text-sm text-hj-gray mt-2 leading-relaxed line-clamp-2">
                          {author.shortBio || "—"}
                        </p>

                        {/* Article info */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-hj-muted flex-wrap">
                          <span className="flex items-center gap-1">
                            <i className="ri-article-line"></i>
                            {info.count} Makale
                          </span>
                          {author.email && (
                            <span className="flex items-center gap-1">
                              <i className="ri-mail-line"></i>
                              {author.email}
                            </span>
                          )}
                        </div>

                        {/* Latest article */}
                        {info.latestTitle && (
                          <div className="mt-3 p-2.5 bg-hj-gray-light rounded-lg">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-hj-gray mb-1">
                              Son Makale
                            </p>
                            <Link
                              to={`/artikel/${info.latestSlug}`}
                              target="_blank"
                              className="text-sm font-medium text-hj-dark hover:text-hj-red transition-colors line-clamp-1"
                            >
                              {info.latestTitle}
                            </Link>
                            {info.latestDate && (
                              <time dateTime={info.latestDate} className="text-xs text-hj-muted mt-0.5 block">
                                {new Date(info.latestDate).toLocaleDateString("de-CH")}
                              </time>
                            )}
                          </div>
                        )}

                        {/* Social links */}
                        {author.socialLinks.length > 0 && (
                          <div className="flex items-center gap-2 mt-3">
                            {author.socialLinks.map((link, i) => (
                              <a
                                key={i}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-7 h-7 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors text-hj-gray"
                                title={link.platform}
                              >
                                <i className={`${socialIcons[link.platform] || "ri-link"} text-xs`}></i>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="px-5 py-3 border-t border-hj-gray-border flex gap-2">
                    <Link
                      to={`/autor/${author.slug}`}
                      target="_blank"
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-hj-gray border border-hj-gray-border rounded hover:bg-hj-gray-light transition-colors text-center"
                    >
                      <i className="ri-eye-line mr-1"></i>
                      Görüntüle
                    </Link>
                    <button
                      onClick={() => openEdit(author)}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-hj-red border border-hj-red rounded hover:bg-hj-red hover:text-white transition-colors text-center"
                    >
                      <i className="ri-edit-line mr-1"></i>
                      Düzenle
                    </button>
                    <button
                      onClick={() => setDeleteModal(author)}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-red-500 border border-red-200 rounded hover:bg-red-50 transition-colors"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>
                      Sil
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-hj-gray-border">
            <i className="ri-user-line text-5xl text-hj-gray mb-4 block"></i>
            <p className="text-lg font-medium text-hj-dark mb-1">Henüz yazar yok.</p>
            <p className="text-sm text-hj-gray mb-6">
              {authorsList.length === 0
                ? "Veritabanında hiç yazar bulunmuyor."
                : "Filtrelere uygun yazar bulunamadı."}
            </p>
            {authorsList.length === 0 && (
              <button
                onClick={openCreate}
                className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors inline-flex items-center gap-2"
              >
                <i className="ri-add-line"></i>
                Yeni Yazar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-hj-gray-border px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-hj-dark">
                {editingAuthor ? "Yazar Düzenle" : "Yeni Yazar"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
                <i className="ri-close-line text-hj-gray"></i>
              </button>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 flex gap-1 border-b border-hj-gray-border">
              {(["general", "bio", "social"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                    activeTab === tab
                      ? "text-hj-red border-hj-red"
                      : "text-hj-gray border-transparent hover:text-hj-dark"
                  }`}
                >
                  {tab === "general" && "Genel"}
                  {tab === "bio" && "Biyografi"}
                  {tab === "social" && "Sosyal Medya"}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-4">
              {activeTab === "general" && (
                <>
                  {/* Avatar Upload */}
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-2">Profil Fotoğrafı</label>
                    <div className="flex items-start gap-4">
                      <div className="shrink-0">
                        {formData.image ? (
                          <div className="relative">
                            <img
                              src={formData.image}
                              alt="Preview"
                              className="w-20 h-20 rounded-full object-cover border-2 border-hj-gray-border"
                            />
                            <button
                              onClick={handleRemoveAvatar}
                              className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                              title="Fotoğrafı kaldır"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </div>
                        ) : (
                          <AvatarPlaceholder name={formData.name} size={80} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-2">
                          <label className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-camera-line text-sm"></i>
                            {avatarUploading || uploadProgress.uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={handleAvatarUpload}
                              disabled={avatarUploading || uploadProgress.uploading}
                            />
                          </label>
                          <p className="text-xs text-hj-gray mt-1">
                            JPG, PNG, WEBP. Max 5 MB.
                          </p>
                        </div>
                        {avatarError && (
                          <p className="text-xs text-red-600 mt-1">{avatarError}</p>
                        )}
                        {(avatarUploading || uploadProgress.uploading) && (
                          <div className="flex items-center gap-2 mt-2">
                            <div className="w-4 h-4 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-xs text-hj-red">{uploadProgress.fileName} yükleniyor...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1">Ad *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            name,
                            slug: editingAuthor ? prev.slug : generateSlug(name),
                          }));
                        }}
                        className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1">Slug *</label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                        className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder="max-mustermann"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1">Rolle *</label>
                      <input
                        type="text"
                        value={formData.role}
                        onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                        className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder="Redakteur"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1">E-Mail</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder="name@helvetik.ch"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-hj-dark mb-1">Tür</label>
                      <select
                        value={formData.author_type}
                        onChange={(e) => setFormData((prev) => ({ ...prev, author_type: e.target.value as AuthorType }))}
                        className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      >
                        <option value="redaktion">Redaktion</option>
                        <option value="autor">Autor</option>
                        <option value="kolumnist">Kolumnist</option>
                        <option value="gastautor">Gastautor</option>
                        <option value="editor">Editor</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                          className="w-4 h-4 rounded border-hj-gray-border text-hj-red focus:ring-hj-red"
                        />
                        <span className="text-sm text-hj-dark">Aktif</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "bio" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1">Kısa Biyografi</label>
                    <input
                      type="text"
                      value={formData.shortBio}
                      onChange={(e) => setFormData((prev) => ({ ...prev, shortBio: e.target.value }))}
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      placeholder="Kısa açıklama..."
                      maxLength={200}
                    />
                    <p className="text-xs text-hj-gray mt-1">{formData.shortBio.length}/200 karakter</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-hj-dark mb-1">Uzun Biyografi</label>
                    <textarea
                      value={formData.longBio}
                      onChange={(e) => setFormData((prev) => ({ ...prev, longBio: e.target.value }))}
                      rows={8}
                      className="w-full px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
                      placeholder="Ayrıntılı biyografi..."
                      maxLength={2000}
                    />
                    <p className="text-xs text-hj-gray mt-1">{formData.longBio.length}/2000 karakter</p>
                  </div>
                </>
              )}

              {activeTab === "social" && (
                <>
                  <div className="flex gap-2">
                    <select
                      value={newSocial.platform}
                      onChange={(e) => setNewSocial((prev) => ({ ...prev, platform: e.target.value }))}
                      className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    >
                      <option value="twitter">Twitter/X</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="instagram">Instagram</option>
                      <option value="github">GitHub</option>
                      <option value="facebook">Facebook</option>
                      <option value="youtube">YouTube</option>
                    </select>
                    <input
                      type="url"
                      value={newSocial.url}
                      onChange={(e) => setNewSocial((prev) => ({ ...prev, url: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      placeholder="https://..."
                    />
                    <button
                      onClick={addSocialLink}
                      className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>

                  <div className="space-y-2">
                    {formData.socialLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-hj-gray-light rounded-lg">
                        <i className={`${socialIcons[link.platform] || "ri-link"} text-hj-gray`}></i>
                        <span className="text-sm font-medium text-hj-dark capitalize">{link.platform}</span>
                        <span className="text-sm text-hj-gray truncate flex-1">{link.url}</span>
                        <button
                          onClick={() => removeSocialLink(i)}
                          className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-red-500 text-sm"></i>
                        </button>
                      </div>
                    ))}
                    {formData.socialLinks.length === 0 && (
                      <p className="text-sm text-hj-gray text-center py-4">Henüz sosyal medya bağlantısı eklenmedi.</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {saveError && (
              <div className="px-6 pb-2">
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  <strong>Hata:</strong> {saveError}
                </div>
              </div>
            )}
            <div className="sticky bottom-0 bg-white border-t border-hj-gray-border px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name.trim() || !formData.slug.trim()}
                className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {editingAuthor ? "Kaydet" : "Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModal(null)}></div>
          <div className="relative bg-white rounded-xl w-full max-w-md p-6">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-lg font-bold text-hj-dark text-center mb-2">Yazar Sil</h3>
            <p className="text-sm text-hj-gray text-center mb-6">
              <strong>{deleteModal.name}</strong> adlı yazarı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              {getInfo(deleteModal.slug).count > 0 && (
                <span className="block mt-1 text-amber-600">
                  <i className="ri-alert-line mr-1"></i>
                  Bu yazarın {getInfo(deleteModal.slug).count} makalesi var. Bunlar yeniden atanmalıdır.
                </span>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 transition-colors cursor-pointer"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}